import { translate, type MessageKey, type MessageParams } from '../i18n/i18n';
import { REPORT_CSS } from './renderHtml';
import { demoWatermarkStyle } from './watermark';

// The 「补充说明 / Addendum」 PDF (Owner decision list 2026-10-19 item 27): issued AFTER delivery, dated and
// signed by the inspector, naming the item it corrects, the reason and the correction, with optional photos
// from the inspector's own library. The original report is never touched. Same page look as the report —
// company block, running header with the property, footer with the licensed inspector, page numbers — and,
// like the English client report, an invisible text layer. Written in English: it goes to the client with
// the English report (the inspector types the content; the hint on the form asks for English).
export interface AddendumModel {
  number: number;
  demo: boolean;
  issuedAt: number;
  reportTitle: string;
  originalIssuedAt: number | null;
  address: string;
  clientName: string;
  item: string;
  reason: string;
  correction: string;
  photos: string[];
  inspectorName: string;
  licenseNumber: string;
  signatureSvg: string | null;
  companyName: string;
  contact: string;
  logo: string | null;
}

const LOCALE = 'en-US' as const;

function t(key: MessageKey, params?: MessageParams): string {
  return translate(LOCALE, key, params);
}

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function paragraphs(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `<p class="rp-p">${esc(line)}</p>`)
    .join('');
}

export function formatAddendumDate(ts: number): string {
  try {
    return new Date(ts).toLocaleDateString(LOCALE, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return new Date(ts).toISOString().slice(0, 10);
  }
}

const ADDENDUM_CSS = `
.rp .ad-sign{display:flex;flex-wrap:wrap;gap:16pt;align-items:flex-end;margin-top:14pt;page-break-inside:avoid}
.rp .ad-sign-box{min-width:180pt}
.rp .ad-sign-box svg{display:block;max-width:220pt;height:auto;border-bottom:1px solid #404943}
.rp .ad-sign-line{margin:2pt 0 0;font-size:10pt;color:#404943}
.rp .ad-statement{margin:12pt 0 0;padding:6pt 8pt;border:1px solid #e2e8f0;border-radius:6pt;font-size:10.5pt}
`;

export function renderAddendumHtml(model: AddendumModel): string {
  const notRecorded = t('addendum.print.notRecorded');
  const inspectorLine = [model.inspectorName, model.licenseNumber.trim().length > 0 ? `${t('report.licenseNumber')} ${model.licenseNumber.trim()}` : ''].filter((s) => s.length > 0).join(' · ');
  const runningHeader = [model.companyName.trim().length > 0 ? model.companyName.trim() : t('addendum.print.title', { title: model.reportTitle }), model.address].join(' · ');
  const watermark = model.demo ? ` style="${esc(demoWatermarkStyle(t('report.demoWatermark')))}"` : '';
  const brand =
    model.companyName.length > 0 || model.contact.length > 0 || model.logo
      ? `<header class="rp-brand" data-brand="true">${model.logo ? `<img class="rp-brand-logo" src="${model.logo}" alt="">` : ''}<div class="rp-brand-text">${model.companyName ? `<p class="rp-brand-name">${esc(model.companyName)}</p>` : ''}${model.contact ? `<p class="rp-brand-contact">${esc(model.contact)}</p>` : ''}</div></header>`
      : '';
  const rows: [string, string][] = [
    [t('addendum.print.property'), model.address.length > 0 ? model.address : notRecorded],
    [t('addendum.print.client'), model.clientName.length > 0 ? model.clientName : notRecorded],
    [t('addendum.print.original'), t('addendum.print.originalLine', { title: model.reportTitle, date: model.originalIssuedAt !== null ? formatAddendumDate(model.originalIssuedAt) : notRecorded })],
    [t('addendum.print.issued'), formatAddendumDate(model.issuedAt)]
  ];
  const photos =
    model.photos.length > 0
      ? `<p class="rp-label">${esc(t('addendum.print.photos'))}</p><div class="rp-photos">${model.photos
          .map((src, index) => `<div class="rp-photo"><img src="${src}" alt=""><p class="rp-cap">${esc(t('addendum.print.photoCaption', { n: index + 1 }))}</p></div>`)
          .join('')}</div>`
      : '';
  return `<style>${REPORT_CSS}${ADDENDUM_CSS}</style><div class="rp rp-addendum"${watermark} data-demo="${model.demo ? 'true' : 'false'}" data-text-layer="true" data-running-header="${esc(runningHeader)}" data-running-footer="${esc(inspectorLine)}" data-page-label="${esc(t('report.page'))}">
${model.demo ? `<div class="rp-demo-banner">${esc(t('report.demoBanner'))}</div>` : ''}${brand}
<h1>${esc(t('addendum.print.title', { title: model.reportTitle }))}</h1>
<p class="rp-sub">${esc(t('addendum.print.number', { n: model.number }))}</p>
<table class="rp-kv">${rows.map(([key, value]) => `<tr><th>${esc(key)}</th><td>${esc(value)}</td></tr>`).join('')}</table>
<h2>${esc(t('addendum.print.item'))}</h2>${paragraphs(model.item)}
<h2>${esc(t('addendum.print.reason'))}</h2>${paragraphs(model.reason)}
<h2>${esc(t('addendum.print.correction'))}</h2>${paragraphs(model.correction)}
${photos}
<p class="ad-statement">${esc(t('addendum.print.statement'))}</p>
<div class="ad-sign"><div class="ad-sign-box">${model.signatureSvg ?? ''}<p class="ad-sign-line">${esc(t('addendum.print.signature'))} · ${esc(t('addendum.print.inspector'))}: ${esc(inspectorLine.length > 0 ? inspectorLine : notRecorded)}</p></div><p class="ad-sign-line">${esc(formatAddendumDate(model.issuedAt))}</p></div>
</div>`;
}
